// MessageList.jsx
import React, { useState, useRef, useEffect } from 'react'
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    Avatar,
    AvatarGroup,
    TextField,
    IconButton,
    Chip,
    Tooltip,
    Divider,
} from '@mui/material'
import MessageInput from './MessageInput'
import InternalLink from './InternalLink'
import { getBackendUserUrl } from '../../utils/utils'
import SendIcon from '@mui/icons-material/Send'
import TagIcon from '@mui/icons-material/LocalOffer'
import ProfileIcon from './ProfileIcon'


// Helper function to safely parse JSON string or handle object
const parseReferenceTag = (tag) => {
    try {
        // If it's already an array, return it
        if (Array.isArray(tag)) {
            return tag
        }

        // If it's an object but not an array, wrap it in array
        if (typeof tag === 'object' && tag !== null) {
            return [tag]
        }

        // If it's a string, parse it and wrap in array if needed
        const parsed = JSON.parse(tag)
        return Array.isArray(parsed) ? parsed : [parsed]
    } catch (error) {
        console.warn('Error parsing referenceTag:', error)
        return [] // Return empty array as fallback
    }
}

function UtteranceList({ conversation = {}, onTagClick, sender, receiver }) {
    const [messages, setMessages] = useState([])
    const [tags, setTags] = useState([])
    const [newMessage, setNewMessage] = useState('')
    const messagesEndRef = useRef(null)
    const [selectedMessage, setSelectedMessage] = useState(null)
    console.log('UtteranceList Conversation', { conversation })
    console.log('UtteranceList Sender', { sender })
    console.log('UtteranceList Receiver', { receiver })

    const sendMessageAndNotify = async (
        messageSummary,
        eventLink,
        referenceTag
    ) => {
        console.log('sendMessageAndNotify params', {
            receiver,
            sender,
            messageSummary,
            eventLink,
            referenceTag,
        })
        try {
            const senderId = sender?.userId 
            const receiverId = receiver?.userId
            const eventId = conversation.eventId

            if (!senderId || !receiverId) {
                console.error('Missing sender or receiver ID')
                return
            }

            const apiUrl =
                getBackendUserUrl() +
                '/send-notification/new_message_db_and_notify'
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    senderId: senderId,
                    receiverId: receiverId,
                    eventId: eventId,
                    content: messageSummary,
                    event_link: eventLink,
                    referenceTag: referenceTag,
                }),
            })
            if (!response.ok) {
                const errorData = await response.json()
                console.error('Server error:', errorData)
                throw new Error(
                    `Server responded with ${response.status}: ${JSON.stringify(errorData)}`
                )
            }
            const data = await response.json()
            console.log('Email notification response:', data)
        } catch (error) {
            console.error('Error sending email notification:', error)
        }
    }

    useEffect(() => {
        if (conversation?.messages) {
            setMessages(conversation.messages)
        } else {
            setMessages([])
        }
        console.log('UtteranceList', { conversation })
    }, [conversation])

    const handleSendMessage = async () => {
        try {
            const newMessageObj = {
                senderId: sender?.userId,
                receiverId: receiver?.userId,
                eventId: conversation.eventId,
                content: newMessage,
                created_at: new Date().toISOString(),
                eventLink: window.location.href,
            }

            // Optimistically add message to UI
            setMessages((prev) => [...prev, newMessageObj])

            // Send to backend
            await sendMessageAndNotify(
                newMessage,
                window.location.href,
                [] // Send empty referenceTag
            )

            // Clear the input
            setNewMessage('')
        } catch (error) {
            console.error('Error sending message:', error)
            // Optionally remove the optimistically added message if there was an error
            setMessages((prev) => prev.slice(0, -1))
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
        })
    }

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [conversation?.messages])

    return (
        <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            background: '#fff',
            borderRadius: 0,
        }}>
            {/* Chat Header */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: 3, 
                    borderBottom: '1px solid #e5e7eb',
                    background: '#fff',
                    borderRadius: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: conversation.participants?.length > 2 
                            ? 'linear-gradient(45deg, #4F46E5, #8a63f2)'
                            : '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        {conversation.participants?.length > 2 ? (
                            <Avatar
                                sx={{ 
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.95rem',
                                    bgcolor: '#4F46E5',
                                }}
                            >
                                {conversation.name?.charAt(0)}
                            </Avatar>
                        ) : (
                            <ProfileIcon
                                user={receiver}
                                size={32}
                            />
                        )}
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 700,
                            color: '#232946',
                            fontSize: '1.15rem',
                            letterSpacing: 0.2,
                        }}>
                            {conversation.name}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.92rem',
                        }}>
                            {conversation.participants?.length > 2 
                                ? `${conversation.participants.length} participants`
                                : 'Direct message'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Messages Area */}
            <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                background: '#f7f8fa',
            }}>
                {conversation?.messages?.map((message, index) => {
                    const isSender = message.senderId === sender?.userId
                    const showAvatar = !isSender && (
                        index === 0 || 
                        conversation.messages[index - 1]?.senderId !== message.senderId
                    )
                    // Show timestamp above first message and when sender changes
                    const showTimestamp =
                        index === 0 ||
                        new Date(message.created_at).toDateString() !== new Date(conversation.messages[index - 1]?.created_at).toDateString() ||
                        message.senderId !== conversation.messages[index - 1]?.senderId
                    return (
                        <React.Fragment key={index}>
                            {showTimestamp && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                        width: '100%',
                                        mb: 1,
                                        fontWeight: 500,
                                    }}
                                >
                                    {new Date(message.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: isSender ? 'flex-end' : 'flex-start',
                                    gap: 0.5,
                                }}
                            >
                                <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    gap: 1,
                                    maxWidth: '80%',
                                }}>
                                    {!isSender && showAvatar && (
                                        <Avatar
                                            sx={{ 
                                                width: 32,
                                                height: 32,
                                                fontSize: '0.92rem',
                                                bgcolor: '#4F46E5',
                                            }}
                                        >
                                            {message.senderName?.charAt(0)}
                                        </Avatar>
                                    )}
                                    {!isSender && !showAvatar && (
                                        <Box sx={{ width: 32 }} />
                                    )}
                                    <Box>
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 3,
                                                background: isSender 
                                                    ? '#4F46E5'
                                                    : '#f3f4f6',
                                                color: isSender ? 'white' : '#232946',
                                                maxWidth: '100%',
                                                wordBreak: 'break-word',
                                                boxShadow: isSender ? '0 2px 8px 0 rgba(79,70,229,0.08)' : 'none',
                                                fontSize: '1rem',
                                            }}
                                        >
                                            <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.5 }}>
                                                {message.content}
                                            </Typography>
                                        </Paper>
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: 'text.secondary',
                                                fontSize: '0.8rem',
                                                ml: 1,
                                            }}
                                        >
                                            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </React.Fragment>
                    )
                })}
                <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: 2, 
                    borderTop: '1px solid #e5e7eb',
                    background: '#fff',
                    borderRadius: 0,
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    gap: 1,
                    alignItems: 'flex-end',
                }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message..."
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                background: '#f7f8fa',
                                '& fieldset': {
                                    borderColor: '#e5e7eb',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#6366F1',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4F46E5',
                                },
                            },
                        }}
                    />
                    <IconButton
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim()}
                        sx={{
                            color: '#4F46E5',
                            borderRadius: 2,
                            '&:hover': {
                                background: 'rgba(78, 70, 229, 0.7)',
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                            },
                        }}
                    >
                        <SendIcon />
                    </IconButton>
                </Box>
            </Paper>
        </Box>
    )
}

export default UtteranceList
